import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganisationMember } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { QueryEmptyState } from '../../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../../components/dashboard/QueryLoadingBlock';
import { formatPersonName, personInitials } from '../../lib/people/personName';
import { formatAdminDate, formatAdminRole } from './adminFormat';

type AdminMembersCardProps = {
  members: OrganisationMember[] | undefined;
  isPending: boolean;
  isError: boolean;
  isRetrying: boolean;
  onRetry: () => void;
};

export function AdminMembersCard({
  members,
  isPending,
  isError,
  isRetrying,
  onRetry,
}: AdminMembersCardProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [inviteOpen, setInviteOpen] = useState(false);
  const count = members?.length ?? 0;
  const ownerCount =
    members?.filter((member) => member.role === 'organisation_owner').length ?? 0;

  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      {inviteOpen ? (
        <ConfirmDialog
          title={t('members.inviteDialog.title')}
          description={t('members.inviteDialog.description')}
          confirmLabel={t('members.inviteDialog.confirm')}
          cancelLabel={t('common:actions.close')}
          onCancel={() => setInviteOpen(false)}
          onConfirm={() => setInviteOpen(false)}
        />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-base font-bold text-ink">
            {count > 0 ? t('members.titleWithCount', { count }) : t('members.title')}
          </h2>
          {ownerCount > 0 ? (
            <p className="m-0 mt-0.5 text-xs text-muted">{t('ownerCount', { count: ownerCount })}</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="rounded-[10px] px-3.5 py-2 text-sm font-bold text-[var(--gs-accent-ink)]"
          style={{ background: 'var(--gs-accent-solid)' }}
        >
          {t('members.invite')}
        </button>
      </div>

      <div className="mt-4">
        {isError || isRetrying ? (
          <QueryErrorState
            message={t('members.loadError')}
            onRetry={onRetry}
            isRetrying={isRetrying}
          />
        ) : isPending ? (
          <QueryLoadingBlock />
        ) : count === 0 ? (
          <QueryEmptyState>{t('members.empty')}</QueryEmptyState>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {members?.map((member) => (
              <li
                key={member.userId}
                className="flex items-center gap-3 rounded-[12px] border border-line px-3 py-2.5"
                style={{ background: 'var(--gs-panel2)' }}
              >
                <span
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-xs font-bold text-ink"
                  style={{ background: 'var(--gs-field)' }}
                  aria-hidden
                >
                  {personInitials(member)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">
                    {formatPersonName(member, member.email)}
                  </span>
                  <span className="block truncate text-xs text-muted">
                    {member.email}
                    {member.displayName
                      ? ` · ${t('members.joined', { date: formatAdminDate(member.createdAt) })}`
                      : null}
                  </span>
                </span>
                <span className="shrink-0 rounded-full border border-line px-2.5 py-1 text-[11px] font-semibold text-muted">
                  {formatAdminRole(member.role)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
